import React, { useContext, useState, useRef } from 'react';
import { FeatureGroup } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import { Modal, DatePicker, Button, Space } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { GlobalContext } from '../../App';
import TCIService from '../../services/TCIService';

const TCIManager = () => {
  const { 
    messageAPI, 
    tciLayers, 
    setTciLayers, 
    showTciLayers, 
    setShowTciLayers 
  } = useContext(GlobalContext);
  
  const [isDateModalVisible, setIsDateModalVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [rectangleGeometry, setRectangleGeometry] = useState(null);
  const [isLoadingTCI, setIsLoadingTCI] = useState(false);
  const featureGroupRef = useRef(null);

  const handleRectangleCreated = (e) => {
    console.log('🟦 TCIManager onCreated called with layerType:', e.layerType);
    // Only handle rectangle creation, ignore other layer types
    if (e.layerType !== 'rectangle') {
      console.log('🟦 TCIManager ignoring layerType:', e.layerType);
      return;
    }
    console.log('🟦 TCIManager processing rectangle');
    
    const layer = e.layer;
    const bounds = layer.getBounds();
    
    // Convert rectangle bounds to polygon coordinates
    const coordinates = [[
      [bounds.getWest(), bounds.getSouth()],
      [bounds.getEast(), bounds.getSouth()],
      [bounds.getEast(), bounds.getNorth()],
      [bounds.getWest(), bounds.getNorth()],
      [bounds.getWest(), bounds.getSouth()]
    ]];
    
    const geometry = {
      crs: {
        type: "name",
        properties: {
          name: "EPSG:4326"
        }
      },
      type: "Polygon",
      coordinates: coordinates
    };
    
    setRectangleGeometry(geometry);
    setIsDateModalVisible(true);
    
    // Style the rectangle with high z-index to appear above TCI
    layer.setStyle({
      color: '#ff4d4f',
      fillColor: '#ff4d4f',
      fillOpacity: 0.2,
      weight: 2
    });
    
    // Ensure rectangle appears above TCI layers
    if (layer.bringToFront) {
      layer.bringToFront();
    }
  };

  const handleDateSubmit = async () => {
    if (!selectedDate || !rectangleGeometry) return;
    
    setIsLoadingTCI(true);
    try {
      const dateString = selectedDate.format('YYYY-MM-DD');
      const response = await TCIService.getTCIData(rectangleGeometry, dateString);
      
      console.log('🌍 TCI Response:', response);
      if (response.data && response.data.imageUrl) {
        const newTciLayer = {
          id: Date.now(),
          imageUrl: response.data.imageUrl,
          date: dateString,
          geometry: rectangleGeometry,
          bounds: [
            [rectangleGeometry.coordinates[0][0][1], rectangleGeometry.coordinates[0][0][0]], // SW
            [rectangleGeometry.coordinates[0][2][1], rectangleGeometry.coordinates[0][2][0]]  // NE
          ]
        };
        
        console.log('📦 New TCI Layer:', newTciLayer);
        setTciLayers(prev => {
          const updated = [...prev, newTciLayer];
          console.log('🗂️ Updated TCI Layers:', updated);
          return updated;
        });
        messageAPI.success('TCI data loaded successfully');
      } else {
        messageAPI.error('Invalid response from TCI service');
      }
    } catch (error) {
      messageAPI.error(error.response?.data?.message || 'Failed to load TCI data');
    } finally {
      // Clear all rectangle layers from the TCI FeatureGroup after TCI is loaded
      if (featureGroupRef.current) {
        featureGroupRef.current.clearLayers();
      }
      setIsLoadingTCI(false);
      setIsDateModalVisible(false);
      setSelectedDate(null);
      setRectangleGeometry(null);
    }
  };

  const handleDateCancel = () => {
    // Clear rectangle layers when user cancels
    if (featureGroupRef.current) {
      featureGroupRef.current.clearLayers();
    }
    setIsDateModalVisible(false);
    setSelectedDate(null);
    setRectangleGeometry(null);
  };

  const toggleTciLayers = () => {
    setShowTciLayers(!showTciLayers);
  };

  return (
    <>
      <FeatureGroup ref={featureGroupRef}>
        <EditControl
          position="topright"
          onCreated={handleRectangleCreated}
          draw={{
            rectangle: true,
            circle: false,
            polyline: false,
            polygon: false,
            marker: false,
            circlemarker: false,
          }}
          edit={{
            edit: false,
            remove: true,
          }}
        />
      </FeatureGroup>

      {/* TCI Toggle Control */}
      {tciLayers.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          zIndex: 1000,
          background: 'white',
          padding: '8px',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          <Button
            size="small"
            icon={showTciLayers ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            onClick={toggleTciLayers}
          >
            TCI Layers ({tciLayers.length})
          </Button>
        </div>
      )}

      <Modal
        title="Select Date for TCI Data"
        open={isDateModalVisible}
        onOk={handleDateSubmit}
        onCancel={handleDateCancel}
        confirmLoading={isLoadingTCI}
        okButtonProps={{ disabled: !selectedDate }}
      >
        <DatePicker
          value={selectedDate}
          onChange={setSelectedDate}
          style={{ width: '100%' }}
          placeholder="Select date"
        />
      </Modal>
    </>
  );
};

export default TCIManager;